import React, { useState, useEffect, useMemo, useCallback } from 'react';
import classnames from 'classnames';
import './FaqPage.scss';
import TextField from 'components/inputs/TextField';
import { useDispatch, useSelector } from 'react-redux';
import { createFaq } from 'constants/faq';
import FaqQuestion from './FaqQuestion';
import FaqDeleteModal from './FaqDeleteModal';
import ButtonNew from 'components/inputs/ButtonNew';
import { faqEditingInitialState } from 'constants/faq';
import {
  fetchFaqs,
  saveFaq,
  addQuestion,
  removeFaq,
  createFaq as addFaqSection,
} from 'actions/faq';
import {
  startEdit,
  setField,
  cancelEdit,
  finishEdit,
} from 'actions/faqSettings';
//
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DnDContainer } from 'components/dragndrop/DnDContainer';

const FaqSectionHeaderEditor = ({ label, onChange, editable, activeFaq }) => {
  const dispatch = useDispatch();

  const [tempValue, setTempValue] = useState(label);
  const [isEditing, setIsEditing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const onComplete = useCallback(() => {
    onChange(tempValue);
    setTempValue(null);
    setIsEditing(false);
  }, [onChange, tempValue]);

  const onCancel = useCallback(() => {
    onChange(label);
    setTempValue(null);
    setIsEditing(false);
  }, [label, onChange]);

  const onDeleteSection = useCallback(() => {
    dispatch(removeFaq(activeFaq?.id));
  }, [dispatch, activeFaq]);

  const onCancelDeleteSection = useCallback(() => {
    setDeleteModalOpen(false);
  }, []);

  useEffect(() => {
    if (isEditing) {
      dispatch(startEdit(activeFaq));
    }
  }, [dispatch, isEditing, activeFaq]);

  useEffect(() => {
    if (label) {
      setTempValue(label);
    }
  }, [label]);

  const onEnter = useCallback(
    event => {
      if (event.keyCode === 13 && !event.shiftKey) {
        onComplete();
      }
    },
    [onComplete]
  );

  return (
    <div
      className="FaqSectionHeaderEditor"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {editable && isEditing ? (
        <>
          <div className="FaqSectionHeaderEditor-button-container">
            <TextField
              onChange={setTempValue}
              value={tempValue ?? label}
              onKeyDown={onEnter}
            />
            <ButtonNew
              label="Ok"
              buttonClasses="secondary-button-small"
              onClick={onComplete}
            />
            <ButtonNew
              label="Cancel"
              buttonClasses="secondary-button-small"
              onClick={onCancel}
            />
          </div>
        </>
      ) : (
        <>
          <div className="FaqSectionHeaderEditor-title">{label}</div>
          {editable && isHovered ? (
            <div className="FaqSectionHeaderEditor-button-container">
              <ButtonNew
                buttonClasses="icon-button-small"
                onClick={() => setIsEditing(true)}
                icon="pencil"
              />
              <ButtonNew
                buttonClasses="icon-button-small"
                onClick={() => setDeleteModalOpen(true)}
                icon="trash"
              />
            </div>
          ) : null}
        </>
      )}
      {deleteModalOpen ? (
        <FaqDeleteModal
          text={`Are you sure you want to delete the ${label} section?`}
          onConfirm={onDeleteSection}
          onCancel={onCancelDeleteSection}
        />
      ) : null}
    </div>
  );
};

const FaqSectionButton = ({ label, onClick, active }) => {
  return (
    <div className="AddFaqSectionButton">
      <ButtonNew
        buttonClasses={classnames('secondary-button-small', {
          inactive: !active,
        })}
        label={label}
        onClick={onClick}
      />
    </div>
  );
};

const AddFaqQuestionButton = ({ faqId }) => {
  const dispatch = useDispatch();

  const onClick = useCallback(() => {
    dispatch(addQuestion(faqId));
    dispatch(saveFaq(faqId));
  }, [faqId, dispatch]);

  return (
    <div className="AddFaqQuestionButton">
      <ButtonNew
        buttonClasses="primary-button-small"
        label="Add an FAQ"
        onClick={onClick}
      />
    </div>
  );
};

// TODO
// - reordering

const FaqPage = () => {
  const dispatch = useDispatch();
  const { isAdmin } = useSelector(state => state.auth.userData);
  // const isAdmin = false;
  const faqs = useSelector(state => state.faq.faq);
  const faqsLoading = useSelector(state => state.faq._loading);
  const faqsLoaded = useSelector(state => state.faq._loaded);
  const editingFaq = useSelector(state => state.faqSettings.faq);

  const [editing, setEditing] = useState(faqEditingInitialState);

  const [activeSection, setActiveSection] = useState(null);

  const activeFaq = useMemo(() => faqs.find(v => v.id === activeSection), [
    faqs,
    activeSection,
  ]);

  const activeQuestions = useMemo(() => {
    const content = activeFaq?.content;
    if (!content) return [];
    return content.sort((a, b) => a.order - b.order);
  }, [activeFaq]);

  const onUpdateSectionName = useCallback(
    nextValue => {
      if (
        nextValue !== undefined &&
        activeFaq?.label !== undefined &&
        nextValue !== activeFaq?.label
      ) {
        const nextFaq = { ...editingFaq, label: nextValue };
        dispatch(setField('label', nextValue));
        dispatch(finishEdit(nextFaq));
        dispatch(saveFaq(nextFaq?.id));
      } else {
        dispatch(cancelEdit());
      }
    },
    [dispatch, activeFaq, editingFaq]
  );

  const onAddFaqSection = useCallback(() => {
    const nextFaq = createFaq('New section', faqs.length + 1);
    dispatch(addFaqSection(nextFaq));
    dispatch(saveFaq(nextFaq?.id));
  }, [dispatch, faqs]);

  // Run once when faqs are populated
  useEffect(() => {
    if (faqs && !activeSection) {
      setActiveSection(faqs?.[0]?.id);
    }
  }, [faqs, activeSection]);

  // fetch FAQs on mount
  useEffect(() => {
    if (!faqsLoading && !faqsLoaded) {
      dispatch(fetchFaqs());
    }
  }, [dispatch, faqsLoading, faqsLoaded]);

  // Reordering
  const [cards, setCards] = useState(null);

  useEffect(() => {
    if (activeQuestions) {
      setCards(
        activeQuestions.map((qa, i) => ({
          id: qa?.id,
          draggable: true,
          component: (
            <FaqQuestion
              key={i}
              sectionId={activeSection}
              questionAnswer={qa}
              editable={isAdmin}
              setEditing={setEditing}
              editing={editing}
              isAdmin={isAdmin}
              activeFaqSection={activeFaq}
            />
          ),
        }))
      );
    }
  }, [activeQuestions, activeSection, isAdmin, editing, activeFaq]);

  useEffect(() => {
    if (
      !isAdmin ||
      !activeQuestions ||
      !cards ||
      !cards.length ||
      !activeQuestions.length
    ) {
      return;
    }
    const cardsUpdated = cards.every(({ id }) =>
      activeQuestions.some(qa => qa.id === id)
    );
    if (!cardsUpdated) return;

    const previousOrder = activeQuestions
      .map(({ id }, i) => ({
        id,
        order: i,
      }))
      .sort((a, b) => a.order - b.order);

    const nextOrder = cards
      .map(({ id }, i) => ({
        id,
        order: i,
      }))
      .sort((a, b) => a.order - b.order);

    const sameOrder = previousOrder.every(
      item => nextOrder.find(v => v.id === item.id)?.order === item.order
    );

    const addedQa = previousOrder.length > nextOrder.length;

    if (!sameOrder && !addedQa) {
      const nextContent = activeQuestions.map(qa => {
        const nextIndex = nextOrder.find(v => qa.id === v.id)?.order;
        return { ...qa, order: nextIndex };
      });

      const nextFaq = { ...editingFaq, content: nextContent };

      dispatch(setField('content', nextContent));
      dispatch(finishEdit(nextFaq));
      dispatch(saveFaq(nextFaq?.id));
    }
  }, [dispatch, activeQuestions, isAdmin, cards, editingFaq]);

  return (
    <div className="FaqPage">
      <div className="FaqPage-content">
        <div className="FaqPage-header">MPG Matrix FAQs</div>
        <div className="FaqPage-table-of-contents">
          {faqs
            .sort((a, b) => a.order - b.order)
            .map((section, i) => (
              <FaqSectionButton
                key={i}
                active={section?.id === activeSection}
                label={section?.label}
                onClick={() => setActiveSection(section?.id)}
              />
            ))}
          {isAdmin ? (
            <ButtonNew
              buttonClasses={'secondary-button-small FaqPage-header-add-button'}
              icon={'plus'}
              onClick={onAddFaqSection}
            />
          ) : null}
        </div>
        {activeFaq ? (
          <FaqSectionHeaderEditor
            label={activeFaq?.label}
            editable={isAdmin}
            onChange={onUpdateSectionName}
            activeFaq={activeFaq}
          />
        ) : null}
        <div className="FaqPage-content-container">
          {isAdmin && cards ? (
            <DndProvider backend={HTML5Backend}>
              <DnDContainer cards={cards} setCards={setCards} />
            </DndProvider>
          ) : (
            activeQuestions.map((qa, i) => (
              <FaqQuestion
                key={i}
                sectionId={activeSection}
                questionAnswer={qa}
                editable={isAdmin}
                setEditing={setEditing}
                editing={editing}
                isAdmin={isAdmin}
                activeFaqSection={activeFaq}
              />
            ))
          )}

          {isAdmin ? <AddFaqQuestionButton faqId={activeSection} /> : null}
        </div>
      </div>
    </div>
  );
};

export default FaqPage;
